// @flow
import 'angieslist-styles/dist/css/bootstrap.css';
import moment from 'moment';
import Loader from 'angieslist-app-platform/lib/browser/Loader';
import { analytics } from 'angieslist-app-platform';
import Snowplow from '@angi/snowplow-react';
import * as authActions from 'angieslist-app-platform/lib/common/auth/authActions';
import ErrorBoundary from 'angieslist-app-platform/lib/browser/error/ErrorBoundary';
import OfficeFooter from 'angieslist-office-app-common/lib/v1/officeFooter';
import { angiLeadsUtils, angiUtils, doesUserHaveDelegatedAccessToBusiness, ANGI_LEADS_ADMIN } from 'angieslist-office-app-common/lib/util/angiLeadsUtils';
import cookieUtils from 'angieslist-app-platform/lib/common/utils/cookieUtils';
import getUnifiedHeaderConfigs from 'angieslist-office-app-common/lib/v1/header/unifiedHeaderConfig';
import DelinquencyAlert from 'angieslist-office-app-common/lib/v1/header/DelinquencyAlert';
import AdvertiseWithUs from 'angieslist-office-app-common/lib/v1/header/AdvertiseWithUs';
import OfficeAppSiteHeader from 'angieslist-office-app-common/lib/v1/header/OfficeAppSiteHeader';
import { Alert, Grid, Row, Col } from 'angieslist-react-components';
import Helmet from 'react-helmet';
import React, { type Node } from 'react';
import PropTypes from 'prop-types';
import { get, find, isEmpty, isUndefined, includes } from 'lodash';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { stylesTypography } from 'angieslist-styles';
import urlCatalog from '@angi/url-catalog';
import { format, subDays } from 'date-fns';
import { SplitFactory, SplitTreatments, SplitSdk } from '@splitsoftware/splitio-react';
import './app.scss';
import GlobalNav from './GlobalNav';
import unauthHeaderConfig from './header/unauthHeaderConfig';
import authHeaderConfig from './header/authHeaderConfig';
import { metas, htmlAttributes } from './meta';
import headerAnalytics from './header/headerAnalytics';
import getServiceProviderIdFromParams from '../common/serviceProvider/getServiceProviderIdFromParams';
import type { BusinessListReducerState } from '../../modules/home/common/businessListReducer';
import serviceProviderActions from '../common/serviceProvider/serviceProviderActions';
import * as advertiseActions from '../common/advertise/advertiseActions';
import type { AdvertiseReducerState } from '../common/advertise/advertiseReducer';
import inboxActions from '../../modules/inbox/common/inboxActions';
import './officeBranding.scss';
import { IntercomClient } from './IntercomClient';
import businessListActions from '../common/businessList/businessListActions';
import profileActions from '../../modules/profile/common/profileActions';
import { splitSdkConfig } from '../../modules/common/splitSdkConfigSetup';
import * as advertisingGatewayActions from '../../modules/adAgreement/common/advertising-gateway/advertisingActions';
import { getServiceProviderUpgradeState } from '../../modules/home/common/selectors/upgradeStateSelectors';
import type { UpgradeStateType } from '../../modules/home/common/selectors/upgradeStateSelectors';
import userHasPermissionToLeadsPro from '../common/utils/angiLeadsUtils';
import configureSnowplow from '../common/utils/analytics/snowplow/configureSnowplow';
import FrontendObservability from './monitoring/FrontendObservability';
import { OnePlatformInboxGate } from './OnePlatformInboxGate';
import { PRO_PRODUCT_PACKAGE_TYPES } from '../../modules/common/commonConstants';

const manifest = require('../common/manifest.json');

const PRO_INBOX_FEATURE_FLAG = 'officeApp_adsProInbox_frontend';
const PRO_INBOX_AUTOMATION_FLAG = 'pro_inbox_automatedmessages_web';
const LEAD_INBOX_FEATURE_FLAG = 'pro_LeadProInbox';
const PRO_LEADS_ENABLE_SUBSCRIPTION_PRO_TYPE_FOR_OPPORTUNITY_LEADS = 'enable_subscription_pro_type_for_opportunity_leads';
const FETCH_UNREAD_COUNT_FROM_CONVERSATION_SERVICE = 'fetch_unread_count_from_conversation_service';

const manifestPath = typeof (manifest) === 'string' ? manifest : '';

const isClient = typeof window !== 'undefined';

const styles = {
  body: {
    paddingTop: stylesTypography.padding.base,
    paddingBottom: stylesTypography.padding.base,
  },
};

const canUserAccessLeadsPro = (user, serviceProviderId) => {
  const entitlements = user ? get(user, 'userInfo.response.entitlements', []) : [];
  return entitlements.length > 0 && userHasPermissionToLeadsPro(entitlements, serviceProviderId);
};

type UnifiedHeaderNavItemProps = {|
  +href: string,
  +subNavSection: string,
  +selectedSubNavItem: string
|};

type UnifiedHeaderNavItemObject = { [key: string]: UnifiedHeaderNavItemProps };

const unifiedHeaderAdsNavItems = serviceProviderId => ({
  // My business
  myBusiness: {
    href: urlCatalog.office.spProfile.edit(serviceProviderId),
    subNavSection: 'myBusiness',
    selectedSubNavItem: 'settings',
  },
  coverageArea: {
    href: urlCatalog.office.ads.spProfile.serviceAreas(serviceProviderId),
    subNavSection: 'myBusiness',
    selectedSubNavItem: 'coverageArea',
  },
  services: {
    href: urlCatalog.office.ads.manageCategories(serviceProviderId),
    subNavSection: 'myBusiness',
    selectedSubNavItem: 'services',
  },
  showcase: {
    href: urlCatalog.office.ads.spProfile.showcases(serviceProviderId),
    subNavSection: 'myBusiness',
    selectedSubNavItem: 'showcase',
  },
  gallery: {
    href: urlCatalog.office.ads.spProfile.images(serviceProviderId),
    subNavSection: 'myBusiness',
    selectedSubNavItem: 'gallery',
  },
  associates: {
    href: urlCatalog.office.ads.associates(serviceProviderId),
    subNavSection: 'myBusiness',
    selectedSubNavItem: 'associates',
  },
  customers: {
    href: urlCatalog.office.bmt.customers(serviceProviderId),
    subNavSection: 'myBusiness',
    selectedSubNavItem: 'customers',
  },
  priceList: {
    href: urlCatalog.office.bmt.priceList(serviceProviderId),
    subNavSection: 'myBusiness',
    selectedSubNavItem: 'priceList',
  },
  // Reviews
  allReviews: {
    href: urlCatalog.office.ads.reviews(serviceProviderId),
    subNavSection: 'reviews',
    selectedSubNavItem: 'allReviews',
  },
  requestReviews: {
    href: urlCatalog.office.ads.requestReviews(serviceProviderId),
    subNavSection: 'reviews',
    selectedSubNavItem: 'requestReviews',
  },
  // Leads
  leads: {
    href: urlCatalog.office.ads.leadBoard(serviceProviderId),
    subNavSection: 'leads',
    selectedSubNavItem: 'board',
  },
}: UnifiedHeaderNavItemObject);

type Props = {
  header: Object,
  showFooter: boolean,
  showHeaderFooter: boolean,
  isAuthenticated: boolean,
  user: Object,
  appName: string,
  children: Node,
  params: Object,
  experiment: Object,
  location: Object,
  serviceProviderCache: any,
  spProductPackage: any,
  proProductPackage: any,
  config: Object,
  logout: Function,
  businessList: BusinessListReducerState,
  updateServiceProviderId: typeof serviceProviderActions.updateServiceProviderId,
  advertiseRequest: typeof advertiseActions.advertiseRequest,
  advertise: AdvertiseReducerState,
  contactCountByTopCategory: typeof advertiseActions.contactCountByTopCategory,
  closeAdvertiseAlert: typeof advertiseActions.closeAdvertiseAlert,
  browser: any,
  getAdsServiceProvider: typeof serviceProviderActions.getAdsServiceProvider,
  getAdsServiceProviderInfo: typeof serviceProviderActions.getAdsServiceProviderInfo,
  businessCenterPhone: Object,
  getNewActivity: typeof businessListActions.getNewActivity,
  getServiceProviderCurrentProfile: typeof profileActions.getServiceProviderCurrentProfile,
  serviceProviderProfile: any,
  setIsInboxEnabled: typeof inboxActions.setIsInboxEnabled,
  setIsAutomationEnabled: typeof inboxActions.setIsAutomationEnabled,
  setFetchUnreadCountFromConversationService:
    typeof inboxActions.setFetchUnreadCountFromConversationService,
  setUnreadMessageCount: typeof inboxActions.setUnreadMessageCount,
  getUnreadMessageCount: typeof inboxActions.getUnreadMessageCount,
  getLeadServiceProvider: typeof serviceProviderActions.getLeadServiceProvider,
  getSpProductPackage: typeof serviceProviderActions.getSpProductPackage,
  getProProductPackage: typeof serviceProviderActions.getProProductPackage,
  inbox: Object,
  leadServiceProvider: Object,
  router: Object,
  serviceProviderUpgradeState: UpgradeStateType,
  fetchAgreementsByServiceProviderId: (spid: string, lastUpdateDate: string) => void;
}

type State = {
  currentSubNav: string,
  selectedSubNavItem: string,
  showHeaderSubNav: boolean,
  originalSubNav: string,
  updateAdvertiseWithUsBanner: boolean,
  sideBarOpen: boolean,
  opportunityTabEnabled: boolean,
  showOpportunityLeadsNavLink: boolean,
  isAngiLeadsAdmin: boolean,
  showDelegateUserBanner: boolean,
  proCity: string,
  proRegion: string,
  proName: string,
  proRating: number,
  proReviewCount: number,
  showMonthlyBudget: boolean,
  showLeadFlow: boolean,
}

export class AppComponent extends React.Component<Props, State> {
  static defaultProps: any;

  constructor(props: Props) {
    super(props);
    this.state = {
      currentSubNav: '',
      selectedSubNavItem: '',
      showHeaderSubNav: false,
      originalSubNav: '',
      updateAdvertiseWithUsBanner: false,
      sideBarOpen: false,
      opportunityTabEnabled: false,
      showOpportunityLeadsNavLink: false,
      isAngiLeadsAdmin: false,
      showDelegateUserBanner: false,
      proCity: '',
      proRegion: '',
      proName: '',
      proRating: 0,
      proReviewCount: 0,
      showMonthlyBudget: false,
      showLeadFlow: false,
    };
  }

  componentWillMount() {
    const {
      params,
      updateServiceProviderId,
    } = this.props;
    const serviceProviderId = getServiceProviderIdFromParams(params);
    updateServiceProviderId(serviceProviderId);

    const navItems = ((
      Object.values(unifiedHeaderAdsNavItems(serviceProviderId)): any
    ): UnifiedHeaderNavItemProps[]);
    const currentPath = get(this.context.router, 'location.pathname', '');
    const maybeSubNav = navItems.find(item => item.href === currentPath);
    const currentSubNav = get(maybeSubNav, 'subNavSection');
    const originalSubNav = get(maybeSubNav, 'subNavSection');
    const selectedSubNavItem = get(maybeSubNav, 'selectedSubNavItem');

    this.setState({
      currentSubNav,
      selectedSubNavItem,
      showHeaderSubNav: !isEmpty(maybeSubNav),
      originalSubNav,
    });
  }


  componentDidMount() {
    const {
      params,
      config,
      fetchAgreementsByServiceProviderId,
      getAdsServiceProviderInfo,
      getLeadServiceProvider,
      getProProductPackage,
      user,
    } = this.props;
    const serviceProviderId = getServiceProviderIdFromParams(params);
    const uriPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const isAngiLeads = angiLeadsUtils.isOnAngiLeadsPage(uriPath);

    if (serviceProviderId
      && !this.props.serviceProviderProfile.currentProfile.response
      && !isAngiLeads) {
      this.props.getServiceProviderCurrentProfile({ serviceProviderId });
    }
    if (serviceProviderId) {
      const client = SplitSdk(splitSdkConfig(config.splitClientSideApiKey)).client();
      client.on(client.Event.SDK_READY, () => {
        const opportunityTabEnable =
          client.getTreatment(PRO_LEADS_ENABLE_SUBSCRIPTION_PRO_TYPE_FOR_OPPORTUNITY_LEADS);
        this.setState({
          opportunityTabEnabled: opportunityTabEnable === 'on',
        });
      });
      const d = subDays(new Date(), 30);
      const formattedDate = format(d, 'yyyy-MM-dd');
      if (!isAngiLeads) {
        fetchAgreementsByServiceProviderId(serviceProviderId, formattedDate);
      }

      if (isAngiLeads && canUserAccessLeadsPro(user, serviceProviderId)) {
        getLeadServiceProvider(serviceProviderId);
        getProProductPackage(serviceProviderId);
      }

      if (!isAngiLeads) {
        // get isAgreementOnlineEnroll value
        getAdsServiceProviderInfo(serviceProviderId);
      }
    }
  }

  componentDidUpdate(prevProps: Props) {
    const {
      serviceProviderCache,
      params,
      contactCountByTopCategory,
      setUnreadMessageCount,
      businessList,
      inbox,
      user,
      getUnreadMessageCount,
      proProductPackage,
      location,
      leadServiceProvider,
      getLeadServiceProvider,
      getProProductPackage,
    } = this.props;
    const serviceProviderId = getServiceProviderIdFromParams(params);
    const daysUntilLockout = get(serviceProviderCache[serviceProviderId], 'delinquency.daysUntilLockout');
    const spId = get(serviceProviderCache[serviceProviderId], 'id');
    const delinquentCookie = cookieUtils.getCookie(`delinquent_alert_viewed-${spId}`);
    const uriPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const isAngiLeads = angiLeadsUtils.isOnAngiLeadsPage(uriPath);


    if (serviceProviderId) {
      const unreadMessageCount = get(businessList, `getNewActivity[${serviceProviderId || ''}].unreadMessageCount`, 0);
      if (!inbox.unreadMessageCount && unreadMessageCount !== inbox.unreadMessageCount) {
        setUnreadMessageCount(unreadMessageCount);
      }
    }

    const hasUserInfo = user && user.userInfo.response;
    const hadNoPrevUserInfo = prevProps.user && !prevProps.user.userInfo.response;
    const canAccess = user && canUserAccessLeadsPro(user, serviceProviderId);

    // Compare by email so we only refetch when a different user is logged in, not when
    // the user prop reference changes (e.g. Redux store update) to avoid duplicate API calls.
    const prevUserEmail = get(prevProps.user, 'userInfo.response.email', '');
    const userEmail = get(user, 'userInfo.response.email', '');
    const userIdentityChanged = hasUserInfo && prevUserEmail !== userEmail;

    const useNewApi = inbox.fetchUnreadCountFromConversationService;
    const prevUseNewApi = get(prevProps.inbox, 'fetchUnreadCountFromConversationService');
    if (isAngiLeads && serviceProviderId && canAccess && useNewApi != null) {
      // Split treatment is set in render when SplitTreatments callback runs with isReady.
      const isSplitReady = prevUseNewApi == null && useNewApi != null;
      const shouldFetchUnreadCount =
        userIdentityChanged || (isSplitReady && hasUserInfo);
      if (shouldFetchUnreadCount) {
        getUnreadMessageCount(serviceProviderId, userEmail, useNewApi);
      }
    }

    if (isAngiLeads && serviceProviderId && canAccess) {
      if (hadNoPrevUserInfo && hasUserInfo) {
        getLeadServiceProvider(serviceProviderId);
        getProProductPackage(serviceProviderId);
      }
    }

    if (serviceProviderId && !this.state.updateAdvertiseWithUsBanner && !isAngiLeads) {
      /* eslint-disable react/no-did-update-set-state */
      this.setState({ updateAdvertiseWithUsBanner: true });
      const startOfDay = moment().startOf('day');
      const endTime = startOfDay.toISOString();
      const startTime = startOfDay.subtract(90, 'days').toISOString();
      contactCountByTopCategory(serviceProviderId, startTime, endTime);
    }

    const isAdvertiser = get(serviceProviderCache[serviceProviderId], 'isAdvertiser', false);
    if (isAdvertiser && !isUndefined(daysUntilLockout) && spId) {
      if (daysUntilLockout < 1) {
        this.redirectSpToPaymentPage(spId, true);
      } else if (!delinquentCookie) {
        this.redirectSpToPaymentPage(spId, false);
      }
    }

    if (serviceProviderId) {
      const packageType = get(proProductPackage, 'response.productPackage.productType');
      const isOpportunitiesOnly = packageType === PRO_PRODUCT_PACKAGE_TYPES.OPPORTUNITIES_ONLY;
      const isProPackageLoading = get(proProductPackage, 'isLoading', true);
      const hasProPackageError = get(proProductPackage, 'error', false);

      // Opportunities tab: non-subscribers always see it; subscribers only when feature flag is on.
      // Use pro package type (not 419) so behavior is consistent across envs.
      const isSubscriptionPro = packageType === PRO_PRODUCT_PACKAGE_TYPES.SUBSCRIPTION;
      if (hasProPackageError || isProPackageLoading) {
        this.updateOpportunityLeadsNavLink(true);
      } else if (packageType) {
        const showTab = isSubscriptionPro ? this.state.opportunityTabEnabled : true;
        this.updateOpportunityLeadsNavLink(showTab);
      } else {
        // Loaded but no package type (e.g. no package yet): treat as non-subscriber, show tab
        this.updateOpportunityLeadsNavLink(true);
      }

      if (hasProPackageError) {
        this.setShowMonthlyBudget(false);
        this.setShowLeadFlow(false);
      } else if (!isProPackageLoading && packageType) {
        this.setShowMonthlyBudget(!isOpportunitiesOnly);
        this.setShowLeadFlow(!isOpportunitiesOnly);
      }
    }

    if (serviceProviderId && this.isLeadsInboxPage(location.pathname)) {
      const serviceProviderIds = get(user, 'userInfo.response.userDelegatedServiceProviderIds', []);
      if (doesUserHaveDelegatedAccessToBusiness(serviceProviderIds, serviceProviderId)) {
        this.setShowDelegateUserBanner(true);
      }
    }
    if (user && serviceProviderId) {
      const entitlements = get(user, 'userInfo.response.entitlements', []);
      // eslint-disable-next-line max-len
      this.setIsAngiLeadAdmin(angiLeadsUtils.getUserPermissionToLeadsBusiness(entitlements, serviceProviderId) === ANGI_LEADS_ADMIN);
      this.setProCity(isAngiLeads
        ? get(leadServiceProvider, 'response.address.city', '')
        : get(serviceProviderCache[serviceProviderId], 'primaryAddress.city.name'));
      this.setProRegion(isAngiLeads
        ? get(leadServiceProvider, 'response.address.stateAbbreviation', '')
        : get(serviceProviderCache[serviceProviderId], 'primaryAddress.region.name'));
      this.setProName(isAngiLeads
        ? get(leadServiceProvider, 'response.name', '')
        : get(serviceProviderCache[serviceProviderId], 'name'));
      this.setProRating(isAngiLeads
        ? get(leadServiceProvider, 'response.ratingInfo.rating', 0)
        : get(serviceProviderCache[serviceProviderId], 'starRating'));
      this.setProReviewCount(isAngiLeads
        ? get(leadServiceProvider, 'response.ratingInfo.reviewCount', 0)
        : get(serviceProviderCache[serviceProviderId], 'starRating'));
    }
  }

  setProCity: ((newValue: string) => void) = (newValue: string) => {
    if (this.state.proCity !== newValue) {
      this.setState({ proCity: newValue });
    }
  };

  setProRegion: ((newValue: string) => void) = (newValue: string) => {
    if (this.state.proRegion !== newValue) {
      this.setState({ proRegion: newValue });
    }
  };

  setProName: ((newValue: string) => void) = (newValue: string) => {
    if (this.state.proName !== newValue) {
      this.setState({ proName: newValue });
    }
  };

  setProRating: ((newValue: number) => void) = (newValue: number) => {
    if (this.state.proRating !== newValue) {
      this.setState({ proRating: newValue });
    }
  };

  setProReviewCount: ((newValue: number) => void) = (newValue: number) => {
    if (this.state.proReviewCount !== newValue) {
      this.setState({ proReviewCount: newValue });
    }
  };

  setIsAngiLeadAdmin: ((newValue: boolean) => void) =
    (newValue: boolean) => {
      if (this.state.isAngiLeadsAdmin !== newValue) {
        this.setState({ isAngiLeadsAdmin: newValue });
      }
    };

  setShowDelegateUserBanner: ((newValue: boolean) => void) =
    (newValue: boolean) => {
      if (this.state.showDelegateUserBanner !== newValue) {
        this.setState({ showDelegateUserBanner: newValue });
      }
    };

  setShowMonthlyBudget: ((newValue: boolean) => void) =
    (newValue: boolean) => {
      if (this.state.showMonthlyBudget !== newValue) {
        this.setState({ showMonthlyBudget: newValue });
      }
    };

  setShowLeadFlow: ((newValue: boolean) => void) =
    (newValue: boolean) => {
      if (this.state.showLeadFlow !== newValue) {
        this.setState({ showLeadFlow: newValue });
      }
    };

  updateOpportunityLeadsNavLink: ((newValue: boolean) => void) =
    (newValue: boolean) => {
      if (this.state.showOpportunityLeadsNavLink !== newValue) {
        this.setState({ showOpportunityLeadsNavLink: newValue });
      }
    };

  redirectSpToPaymentPage: ((spid: string, isLockout: boolean) => void) =
    (spid: string, isLockout: boolean) => {
      const paymentPageHref = `/app/statements/${spid}/preferences/advertising`;
      if (window.location.pathname !== paymentPageHref) {
        if (isLockout) {
          window.location.replace(paymentPageHref);
        } else {
          window.location.href = paymentPageHref;
        }
      }
    }

  isInboxPage: (path: string) => boolean =
    (path: string) => {
      const regex = new RegExp('/app/inbox/.*');
      const matches = regex.exec(path);
      return !isEmpty(matches);
    }

  isLeadsInboxPage: (path: string) => boolean =
    (path: string) => {
      const regex = new RegExp('/app/h/inbox/.*');
      const matches = regex.exec(path);
      return !isEmpty(matches);
    }

  render(): Node {
    const {
      isAuthenticated,
      children,
      header,
      showFooter,
      showHeaderFooter,
      user,
      appName,
      params,
      experiment,
      location,
      serviceProviderCache,
      config,
      logout,
      businessList,
      advertise: {
        advertiseRequest: {
          isLoading: advertiseIsLoading,
          response: advertiseResponse,
          error: advertiseError,
        },
        contactCountByTopCategory: {
          isLoading: contactCountByTopCategoryIsLoading,
          response: contactCountByTopCategoryResponse,
          error: contactCountByTopCategoryError,
        },
      },
      advertiseRequest,
      closeAdvertiseAlert,
      getAdsServiceProvider,
      businessCenterPhone,
      getServiceProviderCurrentProfile,
      setIsInboxEnabled,
      setIsAutomationEnabled,
      setFetchUnreadCountFromConversationService,
      inbox,
    } = this.props;
    const {
      currentSubNav,
      selectedSubNavItem,
      showHeaderSubNav,
      originalSubNav,
      sideBarOpen,
      proCity,
      proRegion,
      proName,
      proRating,
      proReviewCount,
    } = this.state;

    const uriPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const isAngiLeads = angiLeadsUtils.isOnAngiLeadsPage(uriPath);
    const serviceProviderId = getServiceProviderIdFromParams(params);
    const userId = get(user, 'userInfo.response.userId');
    const userFirstName = get(user, 'userInfo.response.firstName', '');
    const userLastName = get(user, 'userInfo.response.lastName', '');
    const userEmail = get(user, 'userInfo.response.email', '');
    const userName = `${userFirstName} ${userLastName}`;
    const entitlements = get(user, 'userInfo.response.entitlements', []);
    const isNorthStar = get(serviceProviderCache[serviceProviderId], 'isNorthStar', false);
    const isAgreementOnlineEnroll = get(serviceProviderCache[serviceProviderId], 'isAgreementActiveAndOnlineEnroll', false);
    const isAdmin = entitlements.find(entitlement => entitlement.permission === 'admin' && entitlement.scopeId === String(serviceProviderId) && entitlement.scope === 'ServiceProvider') || false;
    const isAdvertiser = get(serviceProviderCache[serviceProviderId], 'isAdvertiser', false);
    const delinquency = get(serviceProviderCache[serviceProviderId], 'delinquency', {});
    const daysUntilLockout = get(delinquency, 'daysUntilLockout');
    const pathname = get(location, 'pathname', '');
    const showGlobalDelinquentAlert = !isUndefined(daysUntilLockout)
      && !includes(pathname, 'app/setup')
      && isAdvertiser;
    const showAdvertiseWithUsBanner = !isAdvertiser
      && serviceProviderId
      && this.props.serviceProviderProfile.currentProfile.response !== null
      && !isUndefined(isNorthStar) && !isNorthStar
      && !this.isInboxPage(location.pathname);
    const numberOfBusinesses = entitlements.reduce((uniqueBusinesses, entitlement) => {
      if (entitlement.scope === 'ServiceProvider') {
        uniqueBusinesses.add(entitlement.scopeId);
      }
      return uniqueBusinesses;
    }, new Set()).size;
    const getCurrentUsersServiceProvidersIsLoading = get(businessList, 'getCurrentUsersServiceProviders.isLoading', false);
    const getServiceProviderIsLoading = get(businessList, 'getServiceProvider.isLoading', false);
    const isLoadingBusinessDetails =
      getCurrentUsersServiceProvidersIsLoading || getServiceProviderIsLoading;

    const toggleHeaderSubNav = (newSubNavState, newSubNav) => {
      this.setState({ showHeaderSubNav: newSubNavState, currentSubNav: newSubNav });
    };

    const calculateShowHeaderSubNav = () => {
      if (isAngiLeads) {
        return currentSubNav !== 'opportunities' && currentSubNav !== 'messages' && currentSubNav !== undefined;
      }
      return currentSubNav !== 'leads' && currentSubNav !== 'messages' && currentSubNav !== undefined;
    };

    const resetHeaderSubNav = () => {
      this.setState({ currentSubNav: originalSubNav });
    };

    const setSideBarOpen = (newValue) => {
      this.setState({ sideBarOpen: newValue });
    };

    const unfiedHeaderConfig = {
      ...getUnifiedHeaderConfigs({
        currentSubNav,
        selectedSubNavItem,
        toggleHeaderSubNav,
        resetHeaderSubNav,
        showHeaderSubNav,
        isAdmin,
        isAuth: isAuthenticated,
        isAdvertiser,
        isLoadingBusinessDetails,
        showOpportunityLeadsNavLink: this.state.showOpportunityLeadsNavLink,
        logout,
        spId: serviceProviderId,
        displaySpId: isAngiLeads ? serviceProviderId : get(serviceProviderCache[serviceProviderId], 'id'),
        userId,
        leadsNotificationCount: get(businessList, `getNewActivity[${serviceProviderId || ''}].unreadLeadCount`, 0),
        numberOfBusinesses,
        reviewsNotificationCount: get(businessList, `getNewActivity[${serviceProviderId || ''}].unreadReviewCount`, 0),
        proCity,
        proRegion,
        proName,
        proType: isAngiLeads ? 'Angi Leads' : 'Angi Ads',
        rating: proRating,
        reviewCount: proReviewCount,
        showGlobalDelinquentAlert,
        delinquency,
        showAdvertiseWithUsBanner,
        getAdsServiceProvider,
        isInboxEnabled: inbox.isInboxEnabled,
        isAutomationEnabled: inbox.isAutomationEnabled,
        messagesNotificationCount: inbox.unreadMessageCount,
        getServiceProviderCurrentProfile,
        onCloseSideBar: setSideBarOpen,
        isAngiLeads,
        isNorthStar,
        isAgreementOnlineEnroll,
        isAngiLeadsAdmin: this.state.isAngiLeadsAdmin,
        showLeadFlow: this.state.showLeadFlow,
        showMonthlyBudget: this.state.showMonthlyBudget,
      }),
    };
    const showUnifiedHeader = get(experiment, 'experiments.unifiedHeader.enabled', false);
    const angiAdsHeader = header ||
      (isAuthenticated ? authHeaderConfig(isAdvertiser) : unauthHeaderConfig());

    const isOnBusinessContextPage = angiUtils.isOnBusinessContextPage(uriPath);
    const footerComponent = showFooter === false ?
      null : (<OfficeFooter
        isAuthenticated={isAuthenticated}
        isAdvertiser={isAdvertiser}
        spId={serviceProviderId}
        isAngiLeads={isAngiLeads}
        isUnifiedHeader={showUnifiedHeader}
        businessCenterPhone={businessCenterPhone}
        isInboxEnabled={inbox.isInboxEnabled}
        isOnBusinessContextPage={isOnBusinessContextPage}
      />);
    const showTestUserBanner = find(entitlements, ({ permission }) => permission === 'testUser') || false;
    const googleTagManagerScript =
      `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${config.googleTagManagerId}');`;

    const intercomAppId = get(this.props.config, 'intercomAppId');
    const maybeCurrentServiceProvider = serviceProviderCache[serviceProviderId];
    const maybeIntercomUserHash = get(maybeCurrentServiceProvider, 'intercomUserHash');
    const serviceProviderName = get(maybeCurrentServiceProvider, 'name');
    const isEligibleForIntercom = () => isAdvertiser && intercomAppId && maybeIntercomUserHash && !includes(pathname, 'preferences/advertising') && !includes(pathname, 'payment');
    const showIntercom =
      (maybeCurrentServiceProvider && showUnifiedHeader) ? isEligibleForIntercom() : false;

    const getHeaderConfig = () => {
      const headerConfig = showUnifiedHeader ? unfiedHeaderConfig : angiAdsHeader;
      return headerConfig;
    };

    const snowplowConfig = configureSnowplow(this.props.config);

    const officeApp = (
      <Snowplow configuration={snowplowConfig}>
        <FrontendObservability
          faroEnvironment={this.props.config.faroEnvironment}
          faroSamplingRate={this.props.config.faroSamplingRate}
        />
        <Loader
          element={
            <Row className="category-row text-center" style={{ marginTop: '1em' }}>
              <i className="icon icon-load icon-spin icon-3x text-primary" />
            </Row>}
          loading={Object.keys(experiment.experiments).length === 0}
        >
          <Helmet>
            <script src={`https://pg.angi.com/v1/bundle/${this.props.config.ferootTag}`} type="text/javascript" />
          </Helmet>
          <If condition={serviceProviderId}>
            <SplitFactory config={{
              core: {
                authorizationKey: this.props.config.splitClientSideApiKey,
                key: serviceProviderId,
              },
            }}
            >
              <SplitTreatments names={[
                PRO_INBOX_FEATURE_FLAG,
                PRO_INBOX_AUTOMATION_FLAG,
                LEAD_INBOX_FEATURE_FLAG,
                FETCH_UNREAD_COUNT_FROM_CONVERSATION_SERVICE,
              ]}
              >
                {({ isReady, treatments }) => {
                  const inboxFFResult = isAngiLeads
                    ? treatments[LEAD_INBOX_FEATURE_FLAG].treatment
                    : treatments[PRO_INBOX_FEATURE_FLAG].treatment;
                  const automationFFResult = treatments[PRO_INBOX_AUTOMATION_FLAG].treatment;
                  const unreadCountFromConversationService =
                    treatments[FETCH_UNREAD_COUNT_FROM_CONVERSATION_SERVICE].treatment;
                  if (isReady) {
                    window.analytics.track('Split - Get Treatment Called', {
                      experimentResults: `${PRO_INBOX_FEATURE_FLAG}:${inboxFFResult}`,
                      splitUserId: serviceProviderId,
                      serviceProviderId: serviceProviderId != null ? `${serviceProviderId}` : null,
                    });
                    window.analytics.track('Split - Get Treatment Called', {
                      experimentResults: `${PRO_INBOX_AUTOMATION_FLAG}:${automationFFResult}`,
                      splitUserId: serviceProviderId,
                      serviceProviderId: serviceProviderId != null ? `${serviceProviderId}` : null,
                    });
                    setIsInboxEnabled(inboxFFResult === 'on');
                    setIsAutomationEnabled(automationFFResult === 'on');
                    setFetchUnreadCountFromConversationService(unreadCountFromConversationService === 'on');
                  }
                }}
              </SplitTreatments>
            </SplitFactory>
          </If>
          <div className={(!showHeaderFooter || !inbox.shouldShowHeader) ? 'hide-header' : null}>
            <OfficeAppSiteHeader
              {...getHeaderConfig()}
              {...{ showTestUserBanner }}
              {...{ delegateUserBanner: this.state.showDelegateUserBanner }}
              {...{ appName }}
              className={showUnifiedHeader ? 'office-header' : 'business-center-header'}
              footer={
                (!showHeaderFooter || this.isInboxPage(location.pathname))
                  ? null
                  : footerComponent
              }
              subNav={
                <If condition={isClient && serviceProviderId && !showUnifiedHeader}>
                  <GlobalNav
                    serviceProviderId={serviceProviderId}
                    key={`global-nav-${+serviceProviderId}`}
                    location={location}
                  />
                </If>
              }
              resetHeaderSubNav={resetHeaderSubNav}
              showHeaderSubNav={calculateShowHeaderSubNav()}
              spId={serviceProviderId}
              isAngiLeads={isAngiLeads}
              sidebarOpen={sideBarOpen}
              setSidebarOpen={setSideBarOpen}
            >
              <If condition={showUnifiedHeader}>
                <If condition={showGlobalDelinquentAlert}>
                  <Grid>
                    <Row>
                      <Col xs={11} sm={12}>
                        <DelinquencyAlert
                          delinquency={delinquency}
                          spId={serviceProviderId}
                        />
                      </Col>
                    </Row>
                  </Grid>
                </If>
                <If condition={showAdvertiseWithUsBanner}>
                  <AdvertiseWithUs
                    advertiseRequest={advertiseRequest}
                    advertiseRequestWasSuccessful={!advertiseIsLoading && advertiseResponse}
                    advertiseRequestError={advertiseError}
                    advertiseRequestPending={advertiseIsLoading}
                    contactCountByTopCategoryResponse={contactCountByTopCategoryResponse}
                    contactCountByTopCategoryError={contactCountByTopCategoryError}
                    contactCountByTopCategoryPending={contactCountByTopCategoryIsLoading}
                    closeAlert={closeAdvertiseAlert}
                    activityLocation="Unified Header"
                    serviceProviderId={serviceProviderId}
                    userId={userId}
                  />
                </If>
                <If condition={showIntercom}>
                  <IntercomClient
                    appId={intercomAppId}
                    userId={userId}
                    userName={userName}
                    userHash={maybeIntercomUserHash}
                    email={userEmail}
                    serviceProviderId={serviceProviderId}
                    serviceProviderName={serviceProviderName}
                    browser={this.props.browser}
                  />
                </If>
              </If>

              <Helmet
                {...{ htmlAttributes }}
                meta={metas}
                link={[
                  { rel: 'shortcut icon', href: 'https://media.angi.com/s3fs-public/Angi_Pros_favicon.ico' },
                  { rel: 'manifest', href: manifestPath },
                ]}
                script={[
                  { type: 'text/javascript', innerHTML: googleTagManagerScript },
                ]}
              >
                <title>Angi</title>
              </Helmet>
              <div className="application-body" style={styles.body}>
                <ErrorBoundary
                  errorMessage={
                    <Grid>
                      <Alert bsStyle="danger">
                        There was an error processing your request.
                        Please reload the page or try again later.
                      </Alert>
                    </Grid>
                  }
                >
                  <If condition={isClient}>
                    {children}
                  </If>
                </ErrorBoundary>
              </div>
            </OfficeAppSiteHeader>
          </div>
        </Loader>
      </Snowplow>
    );

    return (
      <OnePlatformInboxGate pathname={pathname}>
        {officeApp}
      </OnePlatformInboxGate>
    );
  }
}

AppComponent.contextTypes = {
  router: PropTypes.object.isRequired,
};

AppComponent.defaultProps = {
  showHeaderFooter: true,
};

export default (compose(
  analytics({
    ruleset: headerAnalytics,
  }),
  connect(state => ({
    isAuthenticated: !!state.auth.isAuthenticated,
    user: state.user,
    appName: state.config.appName,
    experiment: state.experimentV2,
    serviceProviderCache: state.serviceProviderCache,
    config: state.config,
    businessList: state.businessList,
    advertise: state.advertise,
    browser: state.browser,
    businessCenterPhone: state.app.businessCenterPhone,
    serviceProviderProfile: state.serviceProviderProfile,
    inbox: state.inbox,
    serviceProviderUpgradeState: getServiceProviderUpgradeState(state),
    leadServiceProvider: state.leadServiceProvider,
    spProductPackage: state.spProductPackage,
    proProductPackage: state.proProductPackage,
  }), {
    ...serviceProviderActions,
    ...businessListActions,
    ...authActions,
    ...advertiseActions,
    ...profileActions,
    ...inboxActions,
    ...advertisingGatewayActions,
  }),
)(AppComponent): any);



// WEBPACK FOOTER //
// ./src/app/browser/App.js